import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  Skeleton,
  Paper,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import DropdownField from "../../../components/DropdownField";
import DataTable from "../../../components/DataTable";
import api from "../../../api";

const CourseTargetArea = () => {
  const { control, setValue } = useForm();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [filters, setFilters] = useState({
    student_id: "",
    course_id: "",
    subject_id: "",
    threshold: "",
  });

  useEffect(() => {
    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        const response = await api.get("parent/students/names");
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          const mapped = list.map((s) => ({ id: String(s.id), name: s.full_name || s.email || `Student ${s.id}` }));
          setStudents(mapped);
          if (mapped.length === 1) {
            setFilters((prev) => ({ ...prev, student_id: mapped[0].id }));
          }
        } else {
          setStudents([]);
        }
      } catch (err) {
        console.error("Error fetching parent students:", err);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchStudents();
  }, []);

  useEffect(() => {
    if (!filters.student_id) {
      setCourses([]);
      setFilters((prev) => ({ ...prev, course_id: "", subject_id: "" }));
      setValue("course_id", "");
      setValue("subject_id", "");
      return;
    }
    const fetchStudentCourses = async () => {
      setCoursesLoading(true);
      try {
        const response = await api.get(`parent/student/${filters.student_id}/courses`);
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          setCourses(list.map((c) => ({
            id: String(c.id),
            name: c.name,
            subjects: Array.isArray(c.subjects) ? c.subjects : [],
          })));
        } else {
          setCourses([]);
        }
        setFilters((prev) => ({ ...prev, course_id: "", subject_id: "" }));
        setValue("course_id", "");
        setValue("subject_id", "");
      } catch (err) {
        console.error("Error fetching student courses:", err);
        setCourses([]);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchStudentCourses();
  }, [filters.student_id, setValue]);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [data, setData] = useState([]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setValue(key, value);
  };

  const handleRecalculate = async () => {
    if (!filters.student_id) {
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        student_id: filters.student_id,
        ...(filters.threshold && { threshold: filters.threshold }),
        ...(filters.course_id && { course_id: filters.course_id }),
        ...(filters.subject_id && { subject_id: filters.subject_id }),
      });
      const response = await api.get(`parent/course-target-areas?${params}`);
      if (response.data?.success && response.data?.data) {
        setData(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setData([]);
      }
      setPage(0);
    } catch (err) {
      console.error("Error fetching course target areas:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const subjects = useMemo(() => {
    if (!filters.course_id) return [];
    const course = courses.find((c) => String(c.id) === String(filters.course_id));
    return course?.subjects ?? [];
  }, [courses, filters.course_id]);

  const fields = [
    {
      name: "student_id",
      label: "Student Name",
      options: students,
      defaultValue: filters.student_id,
      onChange: (value) => handleFilterChange("student_id", value),
      loading: studentsLoading,
    },
    {
      name: "course_id",
      label: "Course Name",
      options: courses.map((c) => ({ id: c.id, name: c.name })),
      defaultValue: filters.course_id,
      onChange: (value) => {
        handleFilterChange("course_id", value);
        handleFilterChange("subject_id", "");
        setValue("subject_id", "");
      },
      loading: coursesLoading,
    },
    {
      name: "subject_id",
      label: "Subject",
      options: subjects,
      defaultValue: filters.subject_id,
      onChange: (value) => handleFilterChange("subject_id", value),
      loading: false,
    },
  ];

  const columns = useMemo(() => [
    { key: "subject_name", label: "Subject" },
    { key: "topic", label: "Topic" },
    { key: "subtopic", label: "Subtopic" },
    { key: "test", label: "Tests" },
    { key: "score", label: "Score %", render: (row) => row.score != null ? `${row.score}%` : "N/A" },
    { key: "rank", label: "Rank" },
  ], []);

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h5" mb={2}>
        This report will show your student topic and subtopic where their test attempt scores
        are below a certain <b>threshold</b>. The <b>recommended</b> threshold for a topic to be
        displayed in the list is a first attempt score of less than 80%.
        <br />
        You can change the percentage threshold as needed — just click <b>RECALCULATE</b> after adjusting your selections.
      </Typography>

      <Box sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            alignItems: "stretch",
            mb: 2,
            "& .MuiInputBase-root": { height: 56 },
            "& .MuiOutlinedInput-root": { height: 56 },
          }}
        >
          {fields.filter((field) => students.length > 1 || field.name !== "student_id").map((field, index) => (
            <Box key={index} sx={{ flex: "1 1 0", minWidth: 0, width: "100%" }}>
              <DropdownField
                control={control}
                name={field.name}
                label={field.label}
                options={field.options}
                onChange={field.onChange}
                defaultValue={field.defaultValue}
                loading={field.loading}
                compact
              />
            </Box>
          ))}

          <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
            <TextField
              type="number"
              fullWidth
              label="Threshold %"
              placeholder="Threshold %"
              value={filters.threshold}
              onChange={(e) => handleFilterChange("threshold", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography color="text.secondary">%</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: "0 0 auto", alignSelf: "center" }}>
            <Button
              variant="contained"
              onClick={handleRecalculate}
              disabled={loading || !filters.student_id}
            >
              RECALCULATE
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Paper sx={{ p: 2 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1 }} />
            ))}
          </Paper>
        ) : (
          <DataTable
            loading={false}
            data={data}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            isFilterSelected={!!filters.student_id}
            columns={columns}
            onRowClick={(row) => navigate(`/topic/details/${row.id}`)}
          />
        )}
      </Box>
    </Box>
  );
};

export default CourseTargetArea;
